import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { openDataUrl } from "@/lib/openDataUrl";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { isImageFile, isImageMedia, isPdfMedia, mediaStill, processMediaFiles } from "@/lib/livePhotoMedia";

const PDF_LIMIT_BYTES = 4 * 1024 * 1024; // 4 MB raw PDF cap so payloads stay under proxy limits.

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

/**
 * Receipt attachments uploader. Accepts JPG/PNG/HEIC images (auto-compressed
 * via the existing PhotoUploader helper) and PDFs (kept as-is, capped at
 * 4 MB each). Supports multi-select on a single tap.
 *
 * Props mirror MultiPhotoUploader so the call-site shape stays familiar.
 */
export function ReceiptUploader({ value = [], onChange, max = 10, testId = "receipt", colonyId, optional = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const slots = Math.max(0, max - value.length);
    if (slots === 0) {
      toast.error(`You can attach up to ${max} receipts per expense.`);
      return;
    }
    const picked = Array.from(files);
    setBusy(true);
    try {
      const next = [...(value || [])];
      const mediaFiles = picked.filter((file) => isImageFile(file) || file.type.startsWith("video/") || /\.(mov|mp4|webm)$/i.test(file.name));
      for (const f of picked.filter((file) => file.type === "application/pdf").slice(0, slots)) {
        if (f.type === "application/pdf") {
          if (f.size > PDF_LIMIT_BYTES) {
            toast.error(`PDF "${f.name}" is over 4 MB — please trim it before uploading.`);
            continue;
          }
          next.push(await fileToDataUrl(f));
        }
      }
      if (mediaFiles.length) {
        const result = await processMediaFiles(mediaFiles, { max, existingCount: next.length, colonyId, onProgress: setProgress });
        next.push(...result.items);
        if (result.ignoredVideos) toast.message("Select each MOV together with its matching iPhone photo.");
        if (result.capped) toast.message(`Only ${slots} more attachment${slots === 1 ? "" : "s"} allowed.`);
      }
      onChange(next);
    } catch (e) {
      toast.error(e.message || "Could not attach receipt.");
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2" data-testid={`${testId}-block`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Receipts{optional ? " (optional)" : ""}</span>
        <span className="text-[10px] text-muted-foreground">{value.length}/{max}</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((src, idx) => {
          const isPdf = isPdfMedia(src);
          const isImg = isImageMedia(src);
          return (
            <div key={`${testId}-${idx}-${src.slice(-20)}`} className="relative aspect-square border border-border rounded-sm overflow-hidden bg-secondary/30 group" data-testid={`${testId}-tile-${idx}`}>
              {isImg && (
                <LivePhotoSurface value={src} alt={`Receipt ${idx + 1}`} className="w-full h-full cursor-zoom-in" onActivate={() => openDataUrl(mediaStill(src))} testId={`${testId}-media-${idx}`} />
              )}
              {isPdf && (
                <button type="button" onClick={() => openDataUrl(src)} className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition">
                  <FileText className="w-7 h-7" />
                  <span className="text-[10px] uppercase tracking-wider">PDF</span>
                </button>
              )}
              {!isImg && !isPdf && (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <Paperclip className="w-6 h-6" />
                </div>
              )}
              <button
                type="button"
                aria-label="Remove receipt"
                onClick={() => remove(idx)}
                className="absolute top-1 right-1 w-6 h-6 rounded-sm bg-background/95 border border-border flex items-center justify-center text-destructive hover:bg-destructive hover:text-destructive-foreground transition"
                data-testid={`${testId}-remove-${idx}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="aspect-square border-2 border-dashed border-border rounded-sm flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition disabled:opacity-60"
            data-testid={`${testId}-add`}
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
            <span className="text-[10px] uppercase tracking-wider">{busy ? (progress ? `${progress}%` : "Adding…") : "Attach"}</span>
          </button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground flex items-center gap-3">
        <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> JPG / PNG / HEIC</span>
        <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> PDF (≤ 4 MB)</span>
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/quicktime,video/mp4,video/webm,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
